import styled from '@emotion/styled';
import {colors, fonts} from 'ui/config';
import {HrLine, Submit, TextSmall, FormErrorWhite, Img} from 'ui';

export const Container = styled('div')({
  minHeight: '100vh',
  padding: '24px 20px 112px',
  backgroundColor: colors.greyLight,
});

export const Header = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 24,
});

export const BackButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
});

export const ProgressWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  marginBottom: 38,
});

export const ProgressTrack = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 263,
  height: 16,
});

export const ProgressLine = styled('div')({
  position: 'absolute',
  top: '50%',
  left: 8,
  right: 8,
  height: 2,
  backgroundColor: '#D8D8D8',
  transform: 'translateY(-50%)',
});

export const ProgressLineActive = styled('div')(({stepIndex}) => ({
  position: 'absolute',
  top: '50%',
  left: 8,
  height: 2,
  width: stepIndex === 0 ? 48 : stepIndex === 1 ? 155 : 'calc(100% - 16px)',
  backgroundColor: colors.caramel,
  transform: 'translateY(-50%)',
  transition: 'width 200ms ease',
}));

export const ProgressDots = styled('div')({
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
});

export const ProgressDot = styled('span')(({active}) => ({
  display: 'block',
  width: 16,
  height: 16,
  borderRadius: '50%',
  backgroundColor: active ? colors.caramel : '#D8D8D8',
}));

export const ProgressLabels = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: 16,
  textAlign: 'center',
});

export const ProgressLabel = styled('div')(({active}) => ({
  flex: 1,
  color: active ? colors.caramel : colors.darkBrown,
  fontSize: 13,
  lineHeight: '16px',
  fontFamily: fonts.default,
  fontWeight: active ? 500 : 400,
}));

export const TitleBlock = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 24,
  marginBottom: 32,
});

export const Title = styled('h1')({
  margin: 0,
  color: colors.darkBrown,
  fontFamily: fonts.title,
  fontSize: 30,
  lineHeight: '34px',
  textAlign: 'center',
});

export const Divider = styled(HrLine)({
  flexShrink: 0,
});

export const ProductCard = styled('div')({
  display: 'flex',
  gap: 24,
  padding: '16px 20px',
  marginLeft: -20,
  marginRight: -20,
  borderTop: '1px solid #D8D8D8',
  borderBottom: '1px solid #D8D8D8',
});

export const ProductImage = styled(Img)({
  width: 96,
  height: 96,
  flexShrink: 0,
  '& img': {
    width: 96,
    height: 96,
    objectFit: 'contain',
    backgroundColor: colors.white,
  },
});

export const ProductDetails = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 96,
  minWidth: 0,
  flex: 1,
});

export const ProductName = styled('div')({
  color: colors.darkBrown,
  fontSize: 18,
  lineHeight: '24px',
});

export const ProductMeta = styled('div')({
  color: colors.darkBrown,
  fontSize: 14,
  lineHeight: '22px',
});

export const SummaryBlock = styled('div')({
  paddingTop: 16,
});

export const SummaryRow = styled('div')(({topBorder, bold}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: topBorder ? 16 : 0,
  paddingBottom: bold ? 16 : 0,
  borderTop: topBorder ? '1px solid #D8D8D8' : 'none',
  fontSize: bold ? 18 : 14,
  lineHeight: '24px',
  fontWeight: bold ? 800 : 400,
}));

export const SummaryLabel = styled('div')(({emphasis}) => ({
  color: emphasis ? colors.caramel : colors.darkBrown,
  fontWeight: emphasis ? 800 : 400,
}));

export const SummaryValue = styled('div')({
  color: colors.darkBrown,
  fontWeight: 800,
  textAlign: 'right',
});

export const FormBlock = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
});

export const FieldLabel = styled('label')({
  color: colors.grey,
  fontSize: 16,
  lineHeight: '22px',
  marginBottom: 8,
});

export const TextField = styled('input')(({hasError}) => ({
  width: '100%',
  height: 48,
  padding: '13px 16px',
  borderRadius: 4,
  border: `1px solid ${hasError ? '#D80B0B' : '#D8D8D8'}`,
  backgroundColor: colors.white,
  color: colors.darkBrown,
  fontSize: 14,
  lineHeight: '22px',
  outline: 0,
  '&:focus': {
    borderColor: colors.caramel,
  },
}));

export const FieldGroup = styled('div')({
  marginBottom: 16,
});

export const RequiredHint = styled(TextSmall)({
  marginTop: -4,
  marginBottom: 20,
  fontSize: 11,
});

export const ButtonGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginTop: 8,
});

export const PrimaryButton = styled(Submit)({
  width: '100%',
  marginBottom: 0,
  '& span': {
    width: '100%',
    paddingLeft: 20,
    paddingRight: 20,
    fontSize: 16,
  },
});

export const SecondaryButton = styled('button')({
  width: '100%',
  padding: 2,
  border: `1px solid ${colors.caramel}`,
  backgroundColor: colors.white,
  color: colors.caramel,
  fontFamily: fonts.title,
  fontSize: 16,
  fontWeight: 'bold',
  cursor: 'pointer',
  '& span': {
    display: 'block',
    width: '100%',
    padding: '12px 20px',
    border: `1px solid ${colors.caramel}`,
  },
});

export const AddressBlock = styled('div')(({compact}) => ({
  paddingTop: 4,
  paddingBottom: compact ? 24 : 160,
}));

export const AddressTitle = styled('div')({
  color: colors.darkBrown,
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 800,
  marginBottom: 8,
});

export const AddressText = styled('div')({
  color: colors.darkBrown,
  fontSize: 14,
  lineHeight: '22px',
});

export const ErrorBox = styled(FormErrorWhite)({
  marginBottom: 12,
});

export const EmptyState = styled('div')({
  color: colors.darkBrown,
  fontSize: 16,
  lineHeight: '24px',
  textAlign: 'center',
  paddingTop: 40,
});

export const ConfirmationText = styled('p')({
  margin: 0,
  color: colors.darkBrown,
  fontSize: 16,
  lineHeight: '24px',
  textAlign: 'center',
});

export const FooterSpacer = styled('div')({
  height: 16,
});

export const FieldError = styled('div')({
  color: '#D80B0B',
  fontSize: 12,
  lineHeight: '16px',
  marginTop: 8,
});

export const FullWidthButtons = styled(ButtonGroup)({
  width: '100%',
});
